/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing legacy code, not touched in this pass; typing this properly is tracked separately */
import { CSSProperties, FunctionComponent } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  resetAutoCountContainersAction,
  setAutoContainersAction,
  setAutoCountContainersAction,
  setGroup,
  setHasUserToggledAutoCountAction,
  setProduct,
  setTransport,
} from "store";
import { BoxRollType, ContainerItem, PalleteType, ProductType, RootState } from "store";
import { auto_containers, pallet_types } from "utils/data";
import { convertSize, convertUnits, generateId, generateTransport, randomColor } from "utils/utils";

export const ItemsContainer = (Component: FunctionComponent) => {
  // Named (not anonymous) so eslint-plugin-react-hooks recognizes this as a
  // component and allows the hook calls below - it was previously an inline
  // arrow function, which the plugin flagged as hooks "inside a callback".
  const ItemsContainerComponent = (props: any) => {
    const dispatch = useDispatch();
    const store = useSelector((state: RootState) => state.root, shallowEqual);

    const addProduct = (
      name: string,
      type: string,
      length: number,
      width: number,
      height: number,
      weight: number,
      radius: number | null,
      diameter: number | null,
      quantity: number,
      color: CSSProperties["color"],
      tilt: boolean,
      tiltX: boolean,
      tiltY: boolean,
      layers: number | null,
      stack_mass: number | null,
      stack_height: number | null,
      fill_ratio: number,
      rollPlacement: number | null,
      groupIndex: number,
      index: number,
      isStuffingDisabled: boolean
    ) => {
      const newProduct: ProductType = {
        name: name,
        uid: generateId(),
        type: type as BoxRollType,
        size: {
          length: length,
          width: width,
          height: height,
          radius: radius,
          diameter: diameter,
        },
        weight: weight,
        qty: quantity,
        color: color,
        index: index,
        stacking: {
          tilt: tilt,
          tiltX: tiltX,
          tiltY: tiltY,
          layers: layers,
          topWeight: stack_mass,
          fill: fill_ratio,
          height: stack_height,
          rollPlacement: rollPlacement,
        },
        isStuffingDisabled,
      };

      let groups = [...store.groups];
      groups[groupIndex].items.push(newProduct);
      dispatch(setProduct(groups));
    };

    const setConvertedMeasurements = () => {
      const options = store.options;

      const convertPallets = () => {
        const groups = [...store.groups];

        groups.forEach((group: any) => {
          if (group.pallets) {
            const defaultPallet = pallet_types[group.pallets.type] || pallet_types[group.pallets.name];

            const defaultSizes = defaultPallet.size;

            group.pallets = {
              ...group.pallets,
              maxWeight: convertUnits(defaultPallet.maxWeight, options.weightUnits),
              weight: convertUnits(defaultPallet.weight, options.weightUnits),
              depth: convertUnits(defaultPallet.depth, options.lengthUnits),
              size: {
                height: convertUnits(defaultSizes.height, options.lengthUnits),
                length: convertUnits(defaultSizes.length, options.lengthUnits),
                width: convertUnits(defaultSizes.width, options.lengthUnits),
              },
            };
          }
        });

        dispatch(setGroup(groups));
      };

      const convertContainers = () => {
        const defaultContainers = [...store.containers];
        const autoContainers = [...store.autoContainers];

        const getConvertedMeasurements = (containers: any, autoContainers = false) => {
          const updatedContainers = containers.map((container: any) => {
            const defaultContainer = generateTransport(container.attr.type, "mm", "kg");
            const defaultSpaces = defaultContainer.spaces[0];

            return {
              ...container,
              spaces: [
                {
                  width: convertUnits(defaultSpaces.width, options.lengthUnits),
                  height: convertUnits(defaultSpaces.height, options.lengthUnits),
                  length: convertUnits(defaultSpaces.length, options.lengthUnits),
                  maxWeight: convertUnits(defaultSpaces.maxWeight, options.weightUnits),
                },
              ],
            };
          });

          if (autoContainers) {
            dispatch(setAutoContainersAction(updatedContainers));
          } else {
            dispatch(setTransport(updatedContainers));
          }
        };

        getConvertedMeasurements(defaultContainers);
        getConvertedMeasurements(autoContainers, true);
      };

      convertPallets();
      convertContainers();
    };

    const changeProductColor = (groupIndex: number, id: string | number, color: string) => {
      let groups = [...store.groups];
      const currentProduct = groups[groupIndex].items.find((item: any) => item.uid === id);
      if (!currentProduct) return;
      const index = groups[groupIndex].items.indexOf(currentProduct);
      groups[groupIndex].items[index].color = color;
      dispatch(setProduct(groups));
    };

    const removeProduct = (id: number, groupIndex: number) => {
      let groups = [...store.groups];
      groups[groupIndex].items = groups[groupIndex].items.filter((product: any) => {
        if (product.uid !== id) return product;
      });
      dispatch(setProduct(groups));
    };

    const changePalletModal = (pallet: any, groupIndex: number) => {
      const newPallet: PalleteType = {
        uid: pallet.uid,
        color: pallet.color,
        type: pallet.type,
        size: {
          length: pallet.size.length,
          width: pallet.size.width,
          height: pallet.size.height,
        },
        stacking: {
          // square/hex - 6
          fill: pallet.stacking.fill,
          layers: pallet.stacking.layers,
          maxHeight: pallet.stacking.maxHeight ?? Infinity,
        },
        weight: pallet.weight,
        maxWeight: pallet.maxWeight,
        depth: pallet.depth,
        name: pallet.name,
      };

      let groups = [...store.groups];
      groups[groupIndex].pallets = newPallet;

      dispatch(setProduct(groups));
    };

    // todo update product refactor
    const updateProduct = (
      id: string | number,
      name: string,
      type: string,
      length: number,
      width: number,
      height: number,
      weight: number,
      radius: number | null,
      diameter: number | null,
      quantity: number,
      color: CSSProperties["color"],
      tilt: boolean,
      tiltX: boolean,
      tiltY: boolean,
      layers: number | null,
      stack_mass: number | null,
      stack_height: number | null,
      fill_ratio: number,
      rollPlacement: number | null,
      groupIndex: number,
      index: number,
      isStuffingDisabled: boolean
    ) => {
      const newProduct = {
        name: name,
        uid: id,
        type: type,
        size: {
          length,
          width,
          height,
          radius,
          diameter,
        },
        weight: weight,
        qty: quantity,
        color: color,
        index: index,
        stacking: {
          tilt: tilt,
          tiltX: tiltX,
          tiltY: tiltY,
          layers: layers,
          topWeight: stack_mass,
          fill: fill_ratio,
          height: stack_height,
          rollPlacement: rollPlacement,
        },
        isStuffingDisabled,
      };

      let groups = [...store.groups];
      groups[groupIndex].items = groups[groupIndex].items.map((item: any) =>
        item.uid === newProduct.uid ? newProduct : item
      );
      dispatch(setProduct(groups));
    };

    const addGroup = (remainsNear: boolean, fill: number, name?: string) => {
      const newGroup = {
        name: name || `Group #${store.groups.length + 1}`,
        color: randomColor(),
        uid: generateId(),
        pallets: false,
        stacking: {
          remainsNear: remainsNear || null,
          fill: fill || null,
        },
        items: [],
      };
      let groups = [...store.groups, newGroup];
      dispatch(setGroup(groups));
    };

    const changeGroupName = (groupIndex: number, name: string) => {
      let groups = [...store.groups];
      groups[groupIndex].name = name;
      dispatch(setGroup(groups));
    };

    const changeGroupColor = (groupIndex: number, color: string) => {
      let groups = [...store.groups];
      groups[groupIndex].color = color;
      dispatch(setGroup(groups));
    };

    const removeGroup = (groupIndex: number) => {
      let groups = store.groups.filter((_group: any, gIndex: number) => gIndex !== groupIndex);
      dispatch(setGroup(groups));
    };

    const changeLoadingRules = (id: string | number, rule: string) => {
      let containers = store.containers.map((container: any) =>
        container.uid === id ? { ...container, loadType: rule } : container
      );

      dispatch(setTransport(containers));
    };

    const changeGroupIndex = (groupIndex: number, direction: string) => {
      let groups = [...store.groups];
      const currentGroup = groups[groupIndex];
      const prevGroup = groups[groupIndex - 1] ? groups[groupIndex - 1] : null;
      const nextGroup = groups[groupIndex + 1] ? groups[groupIndex + 1] : null;

      if (direction === "up" && prevGroup) {
        groups = groups.map((group, index) => {
          if (index === groupIndex - 1) return currentGroup;
          if (index === groupIndex) return prevGroup;
          return group;
        });
      } else if (direction === "down" && nextGroup) {
        groups = groups.map((group, index) => {
          if (index === groupIndex) return nextGroup;
          if (index === groupIndex + 1) return currentGroup;
          return group;
        });
      }
      dispatch(setGroup(groups));
    };

    const changePallet = (groupIndex: number) => {
      const options = store.options;

      let groups = store.groups.map((group: any, gIndex: number) => {
        if (gIndex === groupIndex) {
          if (!group.pallets) {
            return {
              ...group,
              pallets: {
                ...pallet_types["euro"],
                uid: generateId(),
                color: "#000",
                stacking: {
                  maxHeight: Infinity,
                  layers: 3,
                  fill: 0.85,
                },
                size: convertSize("euro", options),
                depth: convertUnits(pallet_types["euro"].depth, options.lengthUnits),
                weight: convertUnits(pallet_types["euro"].weight, options.weightUnits),
                maxWeight: convertUnits(pallet_types["euro"].maxWeight, options.weightUnits),
              },
            };
          } else {
            return {
              ...group,
              pallets: false,
            };
          }
        }
        return group;
      });
      dispatch(setGroup(groups));
    };

    const changePalletColor = (groupIndex: number, color: string) => {
      let groups = [...store.groups];
      const pallets = groups[groupIndex].pallets;
      if (!pallets || typeof pallets === "boolean") return;
      groups[groupIndex].pallets = { ...pallets, color };
      dispatch(setGroup(groups));
    };

    const changePalletType = (groupIndex: number, type: string, color: string) => {
      const groups = [...store.groups];
      const options = store.options;
      const currentPallets = groups[groupIndex].pallets;
      const stacking =
        currentPallets && typeof currentPallets === "object"
          ? currentPallets.stacking
          : { fill: 0.85, layers: 3, maxHeight: Infinity };

      groups[groupIndex] = {
        ...groups[groupIndex],
        pallets: {
          color,
          ...pallet_types[type],
          stacking,
          uid: generateId(),
          size: convertSize(type, options),
          depth: convertUnits(pallet_types[type].depth, options.lengthUnits),
          weight: convertUnits(pallet_types[type].weight, options.weightUnits),
          maxWeight: convertUnits(pallet_types[type].maxWeight, options.weightUnits),
        },
      };
      dispatch(setGroup(groups));
    };

    const addTransport = (type: string) => {
      const { lengthUnits, weightUnits } = store.options;
      const containers = [...store.containers, generateTransport(type, lengthUnits, weightUnits)];
      dispatch(setTransport(containers));
    };

    const changeTransport = (id: string | number, newType: string) => {
      const { lengthUnits, weightUnits } = store.options;
      let containers = store.containers.map((container: any) =>
        container.uid === id ? generateTransport(newType, lengthUnits, weightUnits) : container
      );
      dispatch(setTransport(containers));
    };

    const changeContainerCount = (id: string | number, count: number) => {
      let containers = store.containers.map((container: any) =>
        container.uid === id ? { ...container, qty: count } : container
      );
      dispatch(setTransport(containers));
    };

    const setAutoContainers = () => {
      if (!store.autoContainers.length) {
        dispatch(setAutoContainersAction(auto_containers));
      } else {
        dispatch(setAutoContainersAction([]));
      }
    };

    const setAutoCount = () => {
      if (!store.hasUserToggledAutoCount) {
        dispatch(setHasUserToggledAutoCountAction());
      }
      dispatch(setAutoCountContainersAction(store.containers as unknown as Pick<ContainerItem, "attr" | "spaces">[]));
    };

    const disableAutoCount = () => {
      dispatch(resetAutoCountContainersAction());
    };

    const removeTransport = (id: number) => {
      let containers = [...store.containers];
      let containersItems = containers.filter((item: any) => {
        if (item.uid !== id) return item;
      });
      const newContainers = [...containersItems];
      dispatch(setTransport(newContainers));
    };

    const changeTransportIndex = (id: string | number, direction: string) => {
      let containers = [...store.containers];
      const currentContainer = containers.find((item: any) => item.uid === id);
      if (!currentContainer) return;
      const index = containers.indexOf(currentContainer);
      const prevContainer = containers[index - 1] ? containers[index - 1] : null;
      const nextContainer = containers[index + 1] ? containers[index + 1] : null;

      if (direction === "up" && prevContainer) {
        containers = containers.map((container, i) => {
          if (i === index - 1) return currentContainer;
          if (i === index) return prevContainer;
          return container;
        });
      } else if (direction === "down" && nextContainer) {
        containers = containers.map((container, i) => {
          if (i === index) return nextContainer;
          if (i === index + 1) return currentContainer;
          return container;
        });
      }
      dispatch(setTransport(containers));
    };

    const changeAllowGroups = (isAllow: boolean, transportId: number) => {
      let containers = store.containers.map((item: any) => {
        if (item.uid === transportId) {
          return {
            ...item,
            allowGroups: isAllow ? null : [0],
          };
        } else {
          return item;
        }
      });
      dispatch(setTransport(containers));
    };

    const setAllowGroups = (index: number, transportId: number) => {
      let containers = store.containers.map((item: any) => {
        if (item.uid === transportId) {
          let groups = Array.isArray(item.allowGroups) ? [...item.allowGroups] : [];
          let groupIndex = groups.findIndex((group: any) => {
            return group === index;
          });
          const updatedGroups =
            groupIndex >= 0 ? groups.filter((_g: any, gIndex: number) => gIndex !== groupIndex) : [...groups, index];

          return {
            ...item,
            allowGroups: updatedGroups,
          };
        } else {
          return item;
        }
      });

      dispatch(setTransport(containers));
    };

    const setGroups = (groups: any) => {
      dispatch(setGroup(groups));
    };

    return (
      <Component
        {...props}
        groupData={store}
        setGroups={setGroups}
        addProduct={addProduct}
        addGroup={addGroup}
        addTransport={addTransport}
        removeProduct={removeProduct}
        removeGroup={removeGroup}
        removeTransport={removeTransport}
        changeProductColor={changeProductColor}
        changeGroupColor={changeGroupColor}
        changePalletColor={changePalletColor}
        changePallet={changePallet}
        changePalletType={changePalletType}
        changePalletModal={changePalletModal}
        changeGroupName={changeGroupName}
        changeAllowGroups={changeAllowGroups}
        changeGroupIndex={changeGroupIndex}
        changeTransportIndex={changeTransportIndex}
        changeLoadingRules={changeLoadingRules}
        changeTransport={changeTransport}
        changeContainerCount={changeContainerCount}
        setAllowGroups={setAllowGroups}
        setAutoContainers={setAutoContainers}
        setAutoCount={setAutoCount}
        disableAutoCount={disableAutoCount}
        updateProduct={updateProduct}
        setConvertedMeasurements={setConvertedMeasurements}
      />
    );
  };

  return ItemsContainerComponent;
};
